import { Column, Loader, Row, Typography } from '@/components'
import { ThemeColor } from '@/enums'
import { useSegment } from '@/modules/analytics'
import { useFormContext } from '@/providers'
import { useKillerAnswers, useSaveKillerAnswers } from '@/services'
import { noop, snakeToCamelCase } from '@/utils'
import { pluck } from 'ramda'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext, useParams } from 'react-router-dom'
import { KillerQuestionsList, NumberedListCounter } from '../styles'

interface CartItem {
  categoryId: string
}

interface Cart {
  [locationId: string]: CartItem[]
}

interface FormValues {
  payment?: {
    paymentId: string
  }
}

interface ButtonState {
  onClick: () => void
  text: string
  loading?: boolean
  disabled?: boolean
}

interface KillerQuestionsStepProps {
  killerQuestions: string[]
  onNext: () => void
  isLast: boolean
  submitCheckout: (params: { paymentId?: string; items: CartItem[] }) => void
  submitCheckoutLoading: boolean
  setButtonState: (state: ButtonState) => void
  setSecondaryButtonState: (state: ButtonState) => void
}

export const KillerQuestionsStep: React.FC<KillerQuestionsStepProps> = ({
  killerQuestions,
  onNext,
  isLast,
  submitCheckout,
  submitCheckoutLoading,
  setButtonState,
  setSecondaryButtonState,
}) => {
  const { t } = useTranslation()
  const { values } = useFormContext<FormValues>()
  const { locationId } = useParams<{ locationId: string }>()
  const { cart } = useOutletContext<{ cart: Cart }>()

  const cartItems = locationId ? cart[locationId] : []

  const { data, isLoading } = useKillerAnswers(
    locationId ?? '',
    pluck('categoryId', cartItems),
  )

  const killerQuestionLabels = t('recommendations.killerQuestions', {
    returnObjects: true,
  }) as Record<string, string>

  const analytics = useSegment()

  const saveKillerQuestionsMutation = useSaveKillerAnswers(locationId ?? '')

  const makeAnswers = useCallback(
    (isAccepted: boolean) => {
      const answerRecord: Record<string, boolean> = {}

      Object.keys(data?.data ?? {}).forEach((key) => {
        answerRecord[key] = isAccepted
      })

      return answerRecord
    },
    [data],
  )

  const handleSubmit = useCallback(
    (answers: Record<string, boolean>) => {
      saveKillerQuestionsMutation.mutate(answers, {
        onSuccess: async () => {
          await analytics.track('app_check_out_killerquestions_success')
          if (isLast) {
            submitCheckout({
              paymentId: values?.payment?.paymentId,
              items: cartItems,
            })
          } else {
            onNext()
          }
        },
      })
    },
    [
      analytics,
      isLast,
      onNext,
      saveKillerQuestionsMutation,
      values?.payment?.paymentId,
      cartItems,
      submitCheckout,
    ],
  )

  useEffect(() => {
    setButtonState({
      onClick: () => handleSubmit(makeAnswers(true)),
      text: t('iConfirm'),
      loading: saveKillerQuestionsMutation.isLoading || submitCheckoutLoading,
    })
    setSecondaryButtonState({
      onClick: () => handleSubmit(makeAnswers(false)),
      text: t('iDoNotConfirm'),
      disabled: saveKillerQuestionsMutation.isLoading || submitCheckoutLoading,
    })

    return () => {
      setButtonState({
        onClick: noop,
        text: t('next'),
        loading: false,
      })
      setSecondaryButtonState({
        onClick: noop,
        text: t('iDoNotConfirm'),
        disabled: false,
      })
    }
  }, [
    handleSubmit,
    makeAnswers,
    saveKillerQuestionsMutation.isLoading,
    submitCheckoutLoading,
    t,
    setButtonState,
    setSecondaryButtonState,
  ])

  if (isLoading) return <Loader />

  return (
    <Column gap="24px">
      <KillerQuestionsList>
        {killerQuestions.map((question: string, index: number) => (
          <li key={question}>
            <Row gap="16px" alignItems="center">
              <NumberedListCounter>{index + 1}</NumberedListCounter>
              <Typography color={ThemeColor.b50} variant="p1Body">
                {killerQuestionLabels[snakeToCamelCase(question)]}
              </Typography>
            </Row>
          </li>
        ))}
      </KillerQuestionsList>
    </Column>
  )
}
